import RepositoryReqIndividual from '@/types/repositoryReqIndividualType';
import apiUrl from './api';
import RepositoryReqOrganization from '@/types/repositoryReqOrganizationType';
import MergedRepositoryReq from '@/types/mergedRepoReqType';
import { logtail } from '@/utils/logtail';
import AuthPayload from '@/types/authPayloadType';
import { postWithAuth } from '@/lib/httpAdapter';
import headerObject from '@/types/headerObj';

const ENTELLIGENCE_ENDPOINT = apiUrl;

/**
 * Extracts a human-readable error message from a caught exception.
 * Used to replace generic 'There was an error' messages with actionable details.
 */
function extractErrorMessage(err: unknown, context: string): string {
  if (err instanceof Error) {
    return `${context}: ${err.message}`;
  }
  if (typeof err === 'string') {
    return `${context}: ${err}`;
  }
  return `${context}: An unexpected error occurred`;
}

/**
 * Safely parses a JSON API response, handling both successful and error HTTP statuses.
 * Returns the parsed response body. If the response is not ok (4xx/5xx), attempts to
 * extract an error message from the response body and returns it as { Error: string }.
 */
async function parseApiResponse(response: Response, context: string) {
  if (!response.ok) {
    let errorDetail = `HTTP ${response.status}`;
    try {
      const body = await response.text();
      const parsed = JSON.parse(body);
      // Backend may return { Error: "..." } or { detail: "..." } or { message: "..." }
      const backendMessage =
        parsed?.Error || parsed?.detail || parsed?.message || parsed?.error;
      if (backendMessage && typeof backendMessage === 'string') {
        errorDetail = backendMessage;
      }
    } catch {
      // Response body wasn't JSON — use the status text
      if (response.statusText) {
        errorDetail = `HTTP ${response.status} ${response.statusText}`;
      }
    }
    return { Error: `${context}: ${errorDetail}` };
  }

  const raw = await response.json();
  return typeof raw === 'string' ? JSON.parse(raw) : raw;
}

const enableContinuousUpdatesForOrg = async (
  OrgName: string,
  OrgUUID: string,
  provider: string,
  token: string,
  headerObj?: headerObject
) => {
  const updateRequest: {
    OrgName: string;
    OrgUUID: string;
  } = {
    OrgName,
    OrgUUID,
  };

  try {
    const response = await postWithAuth(
      `${ENTELLIGENCE_ENDPOINT}setupContinuousUpdatesForOrg/`,
      updateRequest,
      {
        headers: {
          ...headerObj,
        },
      }
    );
    return JSON.parse(await response.json());
  } catch (err: any) {
    logtail.error(`${err} error in enablingContinousUpdatesforOrg`, {
      OrgName,
    });
    logtail.flush();
  }
};

const enablePrBotForOrg = async (
  OrgName: string,
  OrgUUID: string,
  provider: string,
  token: string,
  headerObj?: headerObject
) => {
  const updateRequest: {
    OrgName: string;
    OrgUUID: string;
    GithubAccessToken?: string;
    GitlabAccessToken?: string;
  } = {
    OrgName,
    OrgUUID,
  };

  if (provider === 'github') {
    updateRequest.GithubAccessToken = token;
  } else {
    updateRequest.GitlabAccessToken = token;
  }

  try {
    const response = await postWithAuth(
      `${ENTELLIGENCE_ENDPOINT}setupPRBotForOrg/`,
      updateRequest
    );
    return JSON.parse(await response.json());
  } catch (err: any) {
    logtail.error(`${err} error in enablingContinousUpdatesforOrg`, {
      OrgName,
    });
    logtail.flush();
  }
};

export const enableContinuousUpdatesForRepo = async (
  RepoName: string,
  GithubURL: string,
  provider: string,
  token: string,
  RepoUUID: string,
  headerObj?: headerObject
) => {
  const updateRequest: {
    RepoName: string;
    URL: string;
    RepoUUID: string;
  } = {
    RepoName,
    URL: GithubURL,
    RepoUUID,
  };

  try {
    const response = await postWithAuth(
      `${ENTELLIGENCE_ENDPOINT}setupContinuousUpdatesForRepo/`,
      updateRequest,
      {
        headers: {
          ...headerObj,
        },
      }
    );
    return JSON.parse(await response.json());
  } catch (err: any) {
    logtail.error(`${err} error in enablingContinousUpdatesforOrg`, {
      RepoName,
    });
    logtail.flush();
  }
};

export const enablePrBotForRepo = async (
  RepoName: string,
  GithubURL: string,
  provider: string,
  token: string,
  RepoUUID: string,
  headerObj?: headerObject
) => {
  const updateRequest: {
    RepoName: string;
    RepoUUID: string;
    GithubURL: string;
    GithubAccessToken?: string;
    GitlabAccessToken?: string;
    UserUUID?: string;
    OrgUUID?: string;
  } = {
    RepoName,
    GithubURL,
    RepoUUID,
  };

  if (provider === 'github') {
    updateRequest.GithubAccessToken = token;
  } else {
    updateRequest.GitlabAccessToken = token;
  }

  try {
    const response = await postWithAuth(
      `${ENTELLIGENCE_ENDPOINT}setupPRBotForRepo/`,
      updateRequest
    );
    return JSON.parse(await response.json());
  } catch (err: any) {
    logtail.error(`${err} error in enablingContinousUpdatesforOrg`, {
      RepoName,
    });
    logtail.flush();
  }
};

export const addOrgRepo = async (
  req: RepositoryReqOrganization,
  headerObj?: headerObject
) => {
  if (!req.Url) return {};

  const addRepoRequest: RepositoryReqOrganization = {
    Name: req.Name,
    Url: req.Url,
    IsPublic: req.IsPublic,
    UserUUID: req.UserUUID,
    ...(req.DocumentationUrl && {
      DocumentationUrl: req.DocumentationUrl,
    }),
    Branch: req.Branch,
    AccessToken: req.AccessToken,
    IngestPrs: req.IngestPrs,
    IngestIssues: req.IngestIssues,
  };

  if (req?.OrganizationUUID) {
    addRepoRequest.OrganizationUUID = req.OrganizationUUID;
  }

  try {
    const response = await postWithAuth(
      `${ENTELLIGENCE_ENDPOINT}addRepo2/`,
      addRepoRequest
    );
    return JSON.parse(await response.json());
  } catch (err: any) {
    logtail.error(`${err} error in addOrgRepo`, { req });
    if (err.code === 'ECONNABORTED') {
      logtail.error('The request timed out.');
      return { Error: 'Time out' };
    }
    if (err.code === 'ERR_BAD_RESPONSE') {
      logtail.error('Bad response');
      return { Error: 'Bad response' };
    }
    logtail.flush();
    return { Error: 'There was an error' };
  }
};

export const addUserRepo = async (
  req: RepositoryReqIndividual,
  headerObj?: headerObject
) => {
  if (!req.Url) return {};

  const addRepoRequest: RepositoryReqIndividual = {
    Name: req.Name,
    Url: req.Url,
    IsPublic: req.IsPublic,
    UserUUID: req.UserUUID,
    ...(req.DocumentationUrl && {
      DocumentationUrl: req.DocumentationUrl,
    }),
    Branch: req.Branch,
    AccessToken: req.AccessToken,
    IngestPrs: req.IngestPrs,
    IngestIssues: req.IngestIssues,
  };

  try {
    const response = await postWithAuth(
      `${ENTELLIGENCE_ENDPOINT}addRepo2/`,
      addRepoRequest
    );
    return JSON.parse(await response.json());
  } catch (err: any) {
    logtail.error(`${err} error in addUserRepo`, { req });
    if (err.code === 'ECONNABORTED') {
      logtail.error('The request timed out.');
      return { Error: 'Time out' };
    }
    logtail.flush();
    return { Error: 'There was an error' };
  }
};

export const addMergedRepo = async (
  req: MergedRepositoryReq,
  headerObj?: headerObject
) => {
  const addRepoRequest: MergedRepositoryReq = {
    BaseRepositoryName: req.BaseRepositoryName,
    BaseRepositoryURL: req.BaseRepositoryURL,
    MergeRepositoryName: req.MergeRepositoryName,
    MergeRepositoryURL: req.MergeRepositoryURL,
    IsPublic: req.IsPublic,
    UserUUID: req.UserUUID,
    OrganizationUUID: req.OrganizationUUID,
    ...(req.DocumentationUrl && {
      DocumentationUrl: req.DocumentationUrl,
    }),
  };

  try {
    const response = await postWithAuth(
      `${ENTELLIGENCE_ENDPOINT}addCombinedRepo/`,
      addRepoRequest
    );
    return JSON.parse(await response.json());
  } catch (err: any) {
    logtail.error(`${err} error in addMergedRepo`, { req });
    if (err.code === 'ECONNABORTED') {
      logtail.error('The request timed out.');
      return { Error: 'Time out' };
    }
    logtail.flush();
    return { Error: 'There was an error' };
  }
};

const getIssues = async (
  RepositoryUUID: string,
  UserUUID: string,
  OrganizationUUID: string,
  headerObj?: headerObject
) => {
  if (!RepositoryUUID) return {};

  const getIssuesRequest = {
    RepositoryUUID,
    UserUUID,
    OrganizationUUID,
  };

  try {
    const response = await postWithAuth(
      `${ENTELLIGENCE_ENDPOINT}fetchIssuesForUser/`,
      getIssuesRequest
    );
    return JSON.parse(await response.json());
  } catch (err) {
    logtail.error(`${err} error in getIssues`, {
      RepositoryUUID,
      UserUUID,
      OrganizationUUID,
    });
    logtail.flush();
    return { Error: 'There was an error' };
  }
};

const getPRs = async (
  RepositoryUUID: string,
  UserUUID: string,
  OrganizationUUID: string,
  headerObj?: headerObject
) => {
  if (!RepositoryUUID) return {};

  const getIssuesRequest = {
    RepositoryUUID,
    UserUUID,
    OrganizationUUID,
  };

  try {
    const response = await postWithAuth(
      `${ENTELLIGENCE_ENDPOINT}fetchPRsForUser/`,
      getIssuesRequest
    );
    return JSON.parse(await response.json());
  } catch (err) {
    logtail.error(`${err} error in getPRs`, {
      RepositoryUUID,
      UserUUID,
      OrganizationUUID,
    });
    logtail.flush();
    return { Error: 'There was an error' };
  }
};

// function not being used
const setUpChat = async (
  question: string,
  github: string,
  headerObj?: headerObject
) => {
  const requestBody = {
    question,
    github,
  };

  try {
    const response = await postWithAuth(
      'https://aiswaryasankar--demo-py-answer-questions-dev.modal.run',
      requestBody
    );
    return JSON.parse(await response.json());
  } catch (err) {
    logtail.error(`${err} error in setUpChat`);
    logtail.flush();
    return { Error: 'There was an error' };
  }
};

export const getAllUsers = async (
  OrgUUID: string,
  headerObj?: headerObject
) => {
  const getUsersRequest = {
    OrgUUID,
  };

  try {
    const response = await postWithAuth(
      `${ENTELLIGENCE_ENDPOINT}fetchAllUsersForOrg/`,
      getUsersRequest
    );
    return JSON.parse(await response.json());
  } catch (err) {
    logtail.error(`${err} error in getAllUsers`, { OrgUUID });
    logtail.flush();
    return { Error: 'There was an error' };
  }
};

export const addMember = async (
  Email: string,
  OrganizationUUID: string,
  AccessToken: string,
  IntegrationName: string,
  headerObj?: headerObject,
  CompanyEmail?: string,
  InviteToken?: string,
  InviterUserUUID?: string
) => {
  if (!OrganizationUUID) return {};

  const addMemberRequest = {
    UserEmail: Email,
    OrganizationUUID,
    AccessToken,
    IntegrationName,
    CompanyEmail,
    InviteToken,
    InviterUserUUID,
  };

  try {
    const response = await postWithAuth(
      `${ENTELLIGENCE_ENDPOINT}addMemberToOrganization/`,
      addMemberRequest
    );
    return JSON.parse(await response.json());
  } catch (err) {
    logtail.error(`${err} error in addMember`, {
      Email,
      OrganizationUUID,
    });
    logtail.flush();
    return { Error: 'There was an error' };
  }
};

export const editOrg = async (
  CompanyName: string,
  OrganizationUUID: string,
  GitOrgName?: string,
  /** Provider account id for GitOrgName. Send it whenever the caller knows it:
   * the scope check prefers the id over the name, and the backend clears a
   * stale id when the name changes without one. */
  GitOrgId?: string,
  headerObj?: headerObject
) => {
  if (!OrganizationUUID) return {};

  const editOrgRequest = {
    OrgName: CompanyName,
    OrgUUID: OrganizationUUID,
    GitOrgName,
    ...(GitOrgId ? { GitOrgId } : {}),
  };

  try {
    const response = await postWithAuth(
      `${ENTELLIGENCE_ENDPOINT}createOrganization/`,
      editOrgRequest
    );
    logtail.info(
      'Call made to endpoint createOrganization in function editOrg',
      {
        editOrgRequest,
      }
    );
    logtail.flush();

    return JSON.parse(await response.json());
  } catch (err) {
    logtail.error(`${err} error in editOrg`, {
      CompanyName,
      OrganizationUUID,
    });
    logtail.flush();
    return { Error: 'There was an error' };
  }
};

const getUserInfo = async (UserUUID: string, headerObj?: headerObject) => {
  const getUserRequest = {
    UserUUID,
  };

  try {
    const response = await postWithAuth(
      `${ENTELLIGENCE_ENDPOINT}fetchUser/`,
      getUserRequest
    );
    logtail.info('Call made to endpoint fetchUser in function getUserInfo', {
      getUserRequest,
    });
    logtail.flush();

    return JSON.parse(await response.json());
  } catch (err) {
    logtail.error(`${err} error in getUserInfo`, { UserUUID });
    logtail.flush();
    return { Error: 'There was an error' };
  }
};

interface UserRequest {
  FirstName?: string;
  LastName?: string;
  Email: string;
  Image: string;
  Role: string;
  OrganizationUUID?: string;
  GithubUsername?: string;
  GitlabUsername?: string;
  BitbucketUsername?: string;
  CompanyEmail?: string;
  Designation?: string;
}

export const editUser = async (
  UserUUID: string,
  FirstName: string,
  LastName: string,
  Role: string,
  Image?: string,
  Email?: string
) => {
  const getUserRequest: Record<string, unknown> = {
    UUID: UserUUID,
  };

  if (Role) {
    getUserRequest.Role = Role;
  }

  if (FirstName) {
    getUserRequest.FirstName = FirstName;
  }

  if (LastName) {
    getUserRequest.LastName = LastName;
  }

  if (Image !== undefined && Image !== '') {
    getUserRequest.Image = Image;
  }

  if (Email !== undefined && Email !== '') {
    getUserRequest.Email = Email;
  }

  try {
    const response = await postWithAuth(
      `${ENTELLIGENCE_ENDPOINT}createUser/`,
      getUserRequest
    );

    // Read the raw body once so we can attempt to parse it for both the
    // success and the error paths. response.json() / JSON.parse can throw on
    // non-JSON bodies (HTML 500 pages, plain-text 502s, etc.) — handle that
    // defensively so the response.ok branch always runs and the toast carries
    // a real HTTP status instead of the generic catch-all.
    const bodyText = await response.text();
    let data: any = null;
    if (bodyText) {
      try {
        const parsed = JSON.parse(bodyText);
        data = typeof parsed === 'string' ? JSON.parse(parsed) : parsed;
      } catch (parseErr) {
        logtail.error('editUser response body was not valid JSON', {
          UserUUID,
          status: response.status,
          bodyPreview: bodyText.slice(0, 500),
          parseErr: String(parseErr),
        });
        logtail.flush();
        data = null;
      }
    }

    if (!response.ok) {
      logtail.error('editUser non-2xx response', {
        UserUUID,
        status: response.status,
        body: data ?? bodyText.slice(0, 500),
      });
      logtail.flush();
      return {
        Error:
          (data && (data.Error || data.error)) || `HTTP ${response.status}`,
      };
    }

    if (!data) {
      return { Error: `HTTP ${response.status}` };
    }

    return data;
  } catch (err) {
    logtail.error(`${err} error in editUser`, {
      UserUUID,
      FirstName,
      LastName,
      Role,
    });
    logtail.flush();
    return { Error: 'There was an error' };
  }
};

export const createUser = async (
  FirstName?: string,
  LastName?: string,
  Email?: string,
  Image?: string,
  Role?: string,
  headerObj?: headerObject,
  TeamUUID?: string,
  OrganizationUUID?: string,
  username?: string,
  provider?: string,
  companyEmail?: string,
  designation?: string
) => {
  const getUserRequest: Record<string, unknown> = {};

  if (FirstName) getUserRequest.FirstName = FirstName;
  if (LastName) getUserRequest.LastName = LastName;
  if (Email) getUserRequest.Email = Email;
  if (Image) getUserRequest.Image = Image;
  if (Role) getUserRequest.Role = Role;
  if (OrganizationUUID) getUserRequest.OrganizationUUID = OrganizationUUID;
  if (TeamUUID) getUserRequest.TeamUUID = TeamUUID;
  if (provider === 'github' && username) {
    getUserRequest.GithubUsername = username;
  } else if (provider === 'bitbucket' && username) {
    getUserRequest.BitbucketUsername = username;
  } else if (username) {
    getUserRequest.GitlabUsername = username;
  }

  // Always include CompanyEmail if provided and not empty
  if (
    companyEmail !== undefined &&
    companyEmail !== null &&
    companyEmail.trim() !== ''
  ) {
    getUserRequest.CompanyEmail = companyEmail.trim();
  }

  if (designation && designation.trim() !== '')
    getUserRequest.Designation = designation.trim();

  try {
    const response = await postWithAuth(
      `${ENTELLIGENCE_ENDPOINT}createUser/`,
      getUserRequest,
      {
        headers: {
          ...headerObj,
        },
      }
    );
    logtail.info('Call made to endpoint createUser in function updateUser', {
      getUserRequest,
    });
    logtail.flush();

    return await parseApiResponse(response, 'createUser');
  } catch (err) {
    const errorMessage = extractErrorMessage(err, 'createUser');
    logtail.error(`${err} error in updateUser`, {
      FirstName,
      LastName,
      Email,
      Image,
      Role,
      OrganizationUUID,
    });
    logtail.flush();
    return { Error: errorMessage };
  }
};

export const indUser = async (
  FirstName: string,
  LastName: string,
  Email: string,
  Image: string,
  Role: string,
  headerObj?: headerObject,
  OrganizationUUID?: string,
  username?: string,
  provider?: string,
  companyEmail?: string,
  designation?: string
) => {
  const getUserRequest: UserRequest = {
    Email,
    Image,
    Role,
  };

  if (companyEmail && companyEmail.trim() !== '') {
    getUserRequest.CompanyEmail = companyEmail.trim();
  }

  if (designation && designation.trim() !== '') {
    getUserRequest.Designation = designation.trim();
  }

  if (FirstName) {
    getUserRequest.FirstName = FirstName;
  }

  if (LastName) {
    getUserRequest.LastName = LastName;
  }

  if (OrganizationUUID) {
    getUserRequest.OrganizationUUID = OrganizationUUID;
  }

  if (provider === 'github') {
    getUserRequest.GithubUsername = username ?? '';
  } else if (provider === 'bitbucket') {
    getUserRequest.BitbucketUsername = username ?? '';
  } else {
    getUserRequest.GitlabUsername = username ?? '';
  }

  try {
    const response = await postWithAuth(
      `${ENTELLIGENCE_ENDPOINT}createUser/`,
      getUserRequest,
      {
        headers: {
          ...headerObj,
        },
      }
    );
    logtail.info('Call made to endpoint createUser in function indUser', {
      getUserRequest,
    });
    logtail.flush();

    return await parseApiResponse(response, 'indUser');
  } catch (err) {
    const errorMessage = extractErrorMessage(err, 'indUser');
    logtail.error(`${err} error in indUser`, {
      FirstName,
      LastName,
      Email,
      Image,
      Role,
      OrganizationUUID,
    });
    logtail.flush();
    return { Error: errorMessage };
  }
};

export const getAuthInfo = async (
  UserUUID: string,
  IntegrationName: string,
  OrgUUID?: string,
  headerObj?: headerObject
) => {
  const getAuthRequest: {
    UserUUID: string;
    IntegrationName?: string;
    OrgUUID?: string;
  } = {
    UserUUID,
  };

  if (IntegrationName) {
    getAuthRequest.IntegrationName = IntegrationName;
  }

  if (OrgUUID && OrgUUID?.trim() !== '') {
    getAuthRequest.OrgUUID = OrgUUID;
  }

  try {
    const response = await postWithAuth(
      `${ENTELLIGENCE_ENDPOINT}fetchAuthInfo/`,
      getAuthRequest,
      {
        headers: {
          ProviderName: IntegrationName,
        },
      }
    );
    logtail.info('Call made to endpoint fetchAuthInfo', {
      getAuthRequest,
    });
    logtail.flush();

    const raw = await response.json();
    return typeof raw === 'string' ? JSON.parse(raw) : raw;
  } catch (err) {
    logtail.error(`${err} error in getAuthInfo`);
    logtail.flush();
    return { Error: 'There was an error' };
  }
};

export const fetchOrgAuthInfo = async (OrgUUID: string, UserUUID?: string) => {
  const getAuthRequest: { OrgUUID: string; UserUUID?: string } = {
    OrgUUID,
  };

  if (UserUUID && UserUUID.trim() !== '') {
    getAuthRequest.UserUUID = UserUUID;
  }

  try {
    const response = await postWithAuth(
      `${ENTELLIGENCE_ENDPOINT}fetchAuthInfo/`,
      getAuthRequest,
      {
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );
    logtail.info('Call made to endpoint fetchAuthInfo', {
      getAuthRequest,
    });
    logtail.flush();

    const raw = await response.json();
    return typeof raw === 'string' ? JSON.parse(raw) : raw;
  } catch (err) {
    logtail.error(`${err} error in getAuthInfo`);
    logtail.flush();
    return { Error: 'There was an error' };
  }
};

const tokenExchange = async (
  code: string | null,
  UserUUID: string,
  OrgUUID?: string
) => {
  const tokenRequestBody = {
    Code: code,
    UserUUID,
    OrgUUID,
  };

  try {
    const response = await postWithAuth(
      `${ENTELLIGENCE_ENDPOINT}generateAccessTokenGithubApp/`,
      tokenRequestBody
    );
    const responseData = JSON.parse(await response.json());
    const accessToken = responseData.AccessToken;
    if (accessToken !== null && accessToken !== '') {
      return accessToken;
    }
    return null;
  } catch (error) {
    logtail.error(`${error} error in tokenExchange`, { code, UserUUID });
    logtail.flush();
    return null;
  }
};

export const saveIntegrationsAuthInfo = async (
  UserUUID: string,
  OrgUUID: string,
  IntegrationName: string,
  AccessToken: string,
  Username: string,
  CloudId: string,
  headerObj: headerObject,
  RefreshToken?: string
) => {
  logtail.info('saveIntegrationsAuthInfo called with:', {
    UserUUID,
    OrgUUID,
    IntegrationName,
    AccessTokenExists: Boolean(AccessToken),
    Username,
    CloudIDExists: Boolean(CloudId),
    headerObjExists: Boolean(headerObj),
    RefreshTokenExists: Boolean(RefreshToken),
    apiUrl,
  });
  logtail.flush();

  if (!AccessToken) {
    logtail.error('saveIntegrationsAuthInfo: AccessToken is empty');
    logtail.flush();
    return;
  }

  if (
    IntegrationName !== 'linear' &&
    IntegrationName !== 'devrev' &&
    IntegrationName !== 'betterstack' &&
    IntegrationName !== 'bitbucket' &&
    !CloudId
  ) {
    logtail.error(
      'saveIntegrationsAuthInfo: CloudID is required for non-linear/devrev/betterstack integrations'
    );
    logtail.flush();
    return;
  }

  const payloadSaveAuthInfo: AuthPayload = {
    AccessToken,
    IntegrationName,
    UserUUID,
  };

  if (IntegrationName === 'github') {
    payloadSaveAuthInfo.GithubUserName = Username;
  } else if (IntegrationName === 'bitbucket') {
    payloadSaveAuthInfo.BitbucketUserName = Username;
  } else {
    payloadSaveAuthInfo.GitlabUserName = Username;
  }

  if (OrgUUID && OrgUUID.trim() !== '') {
    payloadSaveAuthInfo.OrgUUID = OrgUUID;
  }

  if (CloudId) {
    payloadSaveAuthInfo.CloudID = CloudId;
  }

  if (RefreshToken) {
    payloadSaveAuthInfo.RefreshToken = RefreshToken;
  }

  if (payloadSaveAuthInfo?.UserUUID || payloadSaveAuthInfo?.OrgUUID) {
    try {
      logtail.info('Calling saveAuthInfo endpoint with payload:', {
        payload: payloadSaveAuthInfo,
      });
      logtail.flush();

      // Self-authenticate this call with the OAuth token we JUST obtained.
      // During a fresh signup the JWT may not be exchanged/stored and the user
      // may not be in the context store yet, so the normal auth header is
      // sometimes empty -> backend 401 -> user stranded with no API key. We
      // already hold the provider's OAuth token here (AccessToken), and the
      // backend's authenticate_user validates an OAuth token in the header, so
      // sending it makes this call succeed regardless of JWT/user-store timing.
      // (If a JWT is present, fetchWithAuth overwrites this with the JWT, which
      // also works.) Scoped to login providers, where AccessToken is a
      // validatable OAuth login token.
      const _authProvider =
        IntegrationName === 'github'
          ? 'github'
          : IntegrationName === 'gitlab'
            ? 'gitlab'
            : IntegrationName === 'bitbucket'
              ? 'bitbucket'
              : undefined;
      const _saveAuthConfig = _authProvider
        ? {
            headers: {
              Authorization: `Bearer ${AccessToken}`,
              ProviderName: _authProvider,
              ...(UserUUID ? { Useruuid: UserUUID } : {}),
            },
          }
        : undefined;
      const response = await postWithAuth(
        `${apiUrl}saveAuthInfo/`,
        payloadSaveAuthInfo,
        _saveAuthConfig
      );
      return JSON.parse(await response.json());
    } catch (error: any) {
      logtail.error('Error in saveIntegrationsAuthInfo:', {
        error: error.message,
        stack: error.stack,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        payloadSaveAuthInfo,
        apiUrl,
      });
      logtail.flush();

      return {
        Error: 'Failed to save integration auth info',
        Details: error.message,
      };
    }
  }

  return { Error: 'Missing UserUUID or OrgUUID' };
};

export const saveAuthInfo = async (payloadSaveAuthInfo: AuthPayload) => {
  const responseSaveAuthInfo = await postWithAuth(
    `${apiUrl}saveAuthInfo/`,
    payloadSaveAuthInfo
  );
  const raw = await responseSaveAuthInfo.json();
  // Handle both JSON string and object responses
  return typeof raw === 'string' ? JSON.parse(raw) : raw;
};

interface AddMemberToOrganizationRequest {
  OrganizationUUID: string;
  AccessToken: string;
  IntegrationName?: string;
  UserEmail?: string;
  UserUUID?: string;
}

async function addMemberToOrganization(
  request: AddMemberToOrganizationRequest
) {
  try {
    const response = await postWithAuth(
      `${apiUrl}addMemberToOrganization/`,
      request
    );

    if (!response.ok) {
      throw new Error(`Failed to add member: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    throw error;
  }
}

/**
 * Fetches integration authentication info for a given integration (e.g., Notion, Confluence).
 * @param {Object} fetchAuthInfoRequest - The request object containing integration and user/org info.
 * @returns {Promise<any>} The integration auth info or error.
 */
export const fetchIntegrationAuthInfo = async (fetchAuthInfoRequest: {
  IntegrationName: string;
  UserUUID?: string;
  OrgUUID?: string;
}) => {
  try {
    const response = await postWithAuth(
      `${ENTELLIGENCE_ENDPOINT}fetchAuthInfo/`,
      fetchAuthInfoRequest
    );
    logtail.info('Call made to endpoint fetchAuthInfo', {
      fetchAuthInfoRequest,
    });
    logtail.flush();
    const raw = await response.json();
    return typeof raw === 'string' ? JSON.parse(raw) : raw;
  } catch (err) {
    logtail.error(`${err} error in fetchIntegrationAuthInfo`);
    logtail.flush();
    return { error: err };
  }
};
